import { z } from 'zod';
import { orderStatuses, serviceCategories } from './domain';

export const coordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

export const phoneSchema = z
  .string()
  .trim()
  .min(7, 'Enter a contact number')
  .max(30)
  .regex(/^[+0-9()\-\s]+$/, 'Enter a valid contact number');

export const orderItemSchema = z.object({
  name: z.string().trim().min(1).max(160),
  quantity: z.number().int().min(1).max(999),
  notes: z.string().trim().max(500).optional(),
});

export const createOrderSchema = z.object({
  serviceCategory: z.enum(serviceCategories),
  pickupSource: z.enum(['directory_branch', 'saved_pickup', 'recent_pickup', 'custom_pin']),
  directoryBranchId: z.uuid().optional(),
  savedPickupId: z.uuid().optional(),
  recentOrderId: z.uuid().optional(),
  pickupName: z.string().trim().min(2).max(160),
  pickupAddress: z.string().trim().max(500),
  pickupInstructions: z.string().trim().max(1_000).optional(),
  pickupCoordinates: coordinatesSchema,
  pickupConfirmed: z.boolean(),
  dropoffAddress: z.string().trim().min(5).max(500),
  dropoffCoordinates: coordinatesSchema,
  deliveryAddressId: z.uuid().optional(),
  deliveryConfirmed: z.boolean(),
  recipientName: z.string().trim().min(2).max(160),
  recipientPhone: phoneSchema,
  items: z.array(orderItemSchema).max(50).default([]),
  parcelDescription: z.string().trim().max(1_000).optional(),
  deliveryInstructions: z.string().trim().max(1_000).optional(),
  purchaseBudgetCentavos: z.number().int().min(0).max(10_000_000),
  fundingSource: z.enum(['rider_advance', 'business_advance', 'not_required']),
  estimatedWeightGrams: z.number().int().min(1).max(500_000),
  packageCount: z.number().int().min(1).max(100),
  packageSize: z.enum(['small', 'medium', 'large', 'bulky']),
  clientRequestId: z.uuid(),
}).superRefine((value, context) => {
  if (!value.pickupConfirmed) {
    context.addIssue({ code: 'custom', path: ['pickupConfirmed'], message: 'Confirm the pickup pin.' });
  }
  if (!value.deliveryConfirmed) {
    context.addIssue({ code: 'custom', path: ['deliveryConfirmed'], message: 'Confirm the delivery pin.' });
  }
  if (value.pickupSource === 'directory_branch' && !value.directoryBranchId) {
    context.addIssue({ code: 'custom', path: ['directoryBranchId'], message: 'Choose a verified store branch.' });
  }
  if (value.pickupSource === 'saved_pickup' && !value.savedPickupId) {
    context.addIssue({ code: 'custom', path: ['savedPickupId'], message: 'Choose a saved pickup.' });
  }
  if (value.pickupSource === 'recent_pickup' && !value.recentOrderId) {
    context.addIssue({ code: 'custom', path: ['recentOrderId'], message: 'Choose a recent pickup.' });
  }
  if (value.serviceCategory !== 'parcel' && value.items.length === 0) {
    context.addIssue({ code: 'custom', path: ['items'], message: 'Add at least one item.' });
  }
  if (value.serviceCategory === 'parcel' && !value.parcelDescription) {
    context.addIssue({ code: 'custom', path: ['parcelDescription'], message: 'Describe the parcel.' });
  }
  if (value.serviceCategory === 'parcel' && (value.purchaseBudgetCentavos !== 0 || value.fundingSource !== 'not_required')) {
    context.addIssue({ code: 'custom', path: ['purchaseBudgetCentavos'], message: 'Prepaid parcel delivery does not use a purchase budget.' });
  }
  if (['food', 'grocery'].includes(value.serviceCategory) && value.purchaseBudgetCentavos <= 0) {
    context.addIssue({ code: 'custom', path: ['purchaseBudgetCentavos'], message: 'Enter the maximum purchase budget.' });
  }
  if (value.purchaseBudgetCentavos > 0 && value.fundingSource === 'not_required') {
    context.addIssue({ code: 'custom', path: ['fundingSource'], message: 'Select who will fund the purchase.' });
  }
  if (value.purchaseBudgetCentavos === 0 && value.fundingSource !== 'not_required') {
    context.addIssue({ code: 'custom', path: ['fundingSource'], message: 'Purchase funding is not needed without a budget.' });
  }
});

export const transitionOrderSchema = z.object({
  orderId: z.uuid(),
  expectedStatus: z.enum(orderStatuses),
  nextStatus: z.enum(orderStatuses),
  reason: z.string().trim().max(500).optional(),
  idempotencyKey: z.uuid(),
});

export const riderLocationSchema = coordinatesSchema.extend({
  orderId: z.uuid().nullable(),
  capturedAt: z.iso.datetime({ offset: true }),
  sequence: z.number().int().nonnegative(),
  accuracyMeters: z.number().nonnegative().max(10_000).optional(),
});

export const riderApplicationSchema = z.object({
  fullName: z.string().trim().min(2).max(160),
  phone: phoneSchema,
  vehicleType: z.string().trim().min(2).max(80),
  vehiclePlate: z.string().trim().max(40).optional(),
});

export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type RiderLocationInput = z.infer<typeof riderLocationSchema>;
